/**
 * "Deine Duelle" auf der Startseite.
 *
 * Beantwortet die Frage, die das Duell vorher offen liess: Wie erfaehrt der
 * Herausforderer, ob jemand geantwortet hat? Bisher gar nicht - der Code
 * verschwand nach dem Teilen. Jetzt merkt sich das Geraet jedes gestartete
 * Duell, und beim Oeffnen der App holt ein einziger Aufruf den Stand aller.
 *
 * Bewusst kein Abruf im Hintergrund und keine Meldung aufs Sperrbildschirm:
 * Beides bedeutete Push-Dienste, eine Erlaubnisabfrage und unter iOS eine
 * installierte App. Fuer ein Spiel dieser Groesse ist das zu viel Apparat -
 * ein Abzeichen beim naechsten Oeffnen genuegt.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchChallengeSummaries, type ChallengeSummary } from '../api'
import { DISCIPLINE_LABELS, variantLabel, type Discipline } from '../engine/disciplines'
import { loadDuels, type StoredDuel } from '../engine/duels'
import { formatDistanceInline, formatTime } from '../engine/units'

/** Beim Sprint zaehlt die Zeit, sonst die Strecke - samt Einheit. */
function formatValue(discipline: Discipline, distanceCm: number, timeMs: number): string {
  return discipline === 'sprint' ? `${formatTime(timeMs)} s` : formatDistanceInline(distanceCm)
}

export function DuelList() {
  const [duels] = useState<StoredDuel[]>(loadDuels)
  const [summaries, setSummaries] = useState<Map<string, ChallengeSummary>>(new Map())

  useEffect(() => {
    if (duels.length === 0) return
    let cancelled = false

    void fetchChallengeSummaries(duels.map((duel) => duel.code)).then((response) => {
      if (cancelled || !response.ok) return
      setSummaries(new Map(response.data.map((entry) => [entry.code, entry])))
    })

    return () => {
      cancelled = true
    }
  }, [duels])

  if (duels.length === 0) return null

  return (
    <section className="duels">
      <h2 className="section">Deine Duelle</h2>
      <div className="duels__list">
        {duels.map((duel) => {
          const summary = summaries.get(duel.code)
          const own = formatValue(duel.discipline, duel.distanceCm, duel.timeMs)
          const fresh = summary ? Math.max(0, summary.attempts - duel.seenAttempts) : 0
          const variant = variantLabel(duel.variant)

          return (
            <Link
              key={duel.code}
              to={`/duell/${duel.code}`}
              className="duels__row"
              data-discipline={duel.discipline}
            >
              <span className="duels__meta">
                <span className="duels__discipline">
                  {DISCIPLINE_LABELS[duel.discipline]}
                  {variant ? ` · ${variant}` : ''}
                </span>
                <span className="duels__own">dein Wert {own}</span>
              </span>

              <span className="duels__status">{renderStatus(duel, summary)}</span>

              {fresh > 0 && <span className="duels__badge">{fresh}</span>}
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function renderStatus(duel: StoredDuel, summary: ChallengeSummary | undefined) {
  if (!summary) return <span className="duels__pending">…</span>
  if (summary.attempts === 0) {
    return <span className="duels__pending">wartet auf Antwort</span>
  }

  const isSprint = duel.discipline === 'sprint'
  const rivalValue = formatValue(
    duel.discipline,
    summary.bestDistanceCm ?? 0,
    summary.bestTimeMs ?? 0,
  )

  // Wer fuehrt? Beim Sprint gewinnt die kuerzere Zeit, sonst die groessere Weite.
  const leading = isSprint
    ? duel.timeMs <= (summary.bestTimeMs ?? Infinity)
    : duel.distanceCm >= (summary.bestDistanceCm ?? 0)

  return (
    <>
      <span className={leading ? 'duels__verdict duels__verdict--ahead' : 'duels__verdict'}>
        {leading ? 'Du führst' : 'Du liegst zurück'}
      </span>
      <span className="duels__rival">
        {summary.bestName}: {rivalValue}
      </span>
    </>
  )
}
